import { el } from './dom';

/**
 * The "choose a new password" screen.
 *
 * Built once and updated rather than rebuilt, for the same reason as the login form -
 * see `src/ui/login.ts`. These are password fields, they hold what the user typed, and
 * a password manager's inline menu is anchored to them; replacing the input under an
 * open menu leaves the menu on screen with nothing behind it.
 */

/** Matches the floor `functions/api/password.ts` enforces. */
export const MIN_PASSWORD_LENGTH = 8;

export interface PasswordViewProps {
  /** True when the server is demanding this, rather than the user choosing to. */
  forced: boolean;
  pending: boolean;
  error: string | null;
  onSubmit: (currentPassword: string, newPassword: string) => void;
}

export interface PasswordView {
  node: HTMLElement;
  update: (state: { pending: boolean; error: string | null }) => void;
  destroy: () => void;
}

export function passwordView(props: PasswordViewProps): PasswordView {
  let pending = props.pending;

  const current = el('input', {
    id: 'current-password',
    name: 'current-password',
    type: 'password',
    autocomplete: 'current-password',
    required: true,
  });

  const next = el('input', {
    id: 'new-password',
    name: 'new-password',
    type: 'password',
    autocomplete: 'new-password',
    minlength: MIN_PASSWORD_LENGTH,
    required: true,
  });

  const confirm = el('input', {
    id: 'confirm-password',
    name: 'confirm-password',
    type: 'password',
    autocomplete: 'new-password',
    minlength: MIN_PASSWORD_LENGTH,
    required: true,
  });

  const errorNode = el('p', { class: 'error' });
  const submit = el('button', { class: 'btn btn--primary', type: 'submit' });

  const update = (state: { pending: boolean; error: string | null }): void => {
    pending = state.pending;
    submit.disabled = state.pending;
    submit.textContent = state.pending ? 'Saving…' : 'Set new password';
    errorNode.textContent = state.error ?? '';
  };

  const form = el(
    'form',
    {
      class: 'panel stack',
      onsubmit: (event: Event) => {
        event.preventDefault();
        if (pending) return;

        // Checked here as well as on the server: nothing is echoed back, so a typo in
        // a field the user cannot read would otherwise become a password nobody knows.
        if (next.value !== confirm.value) {
          update({ pending: false, error: 'The two new passwords do not match.' });
          return;
        }
        if (next.value.length < MIN_PASSWORD_LENGTH) {
          update({
            pending: false,
            error: `Use at least ${MIN_PASSWORD_LENGTH} characters.`,
          });
          return;
        }
        if (next.value === current.value) {
          update({ pending: false, error: 'That is the password you already have.' });
          return;
        }
        props.onSubmit(current.value, next.value);
      },
    },
    el(
      'p',
      { class: 'muted' },
      props.forced
        ? 'Your password was set by someone else, so it has to be replaced before ' +
            'you can go any further.'
        : 'Choose something only you know.',
    ),
    el(
      'div',
      { class: 'field' },
      el('label', { for: 'current-password' }, 'Current password'),
      current,
    ),
    el(
      'div',
      { class: 'field' },
      el('label', { for: 'new-password' }, 'New password'),
      next,
    ),
    el(
      'div',
      { class: 'field' },
      el('label', { for: 'confirm-password' }, 'New password again'),
      confirm,
    ),
    errorNode,
    submit,
  );

  const node = el('div', { class: 'grow' }, form);

  update(props);
  // Once, on the way in - re-focusing on every update would fight both the user and an
  // autofill menu anchored to another field.
  queueMicrotask(() => current.focus());

  return {
    node,
    update,
    destroy: () => {
      if (node.contains(document.activeElement)) {
        (document.activeElement as HTMLElement).blur();
      }
      // Three passwords, none of which should sit in a detached node for the rest of
      // the session.
      for (const input of [current, next, confirm]) input.value = '';
    },
  };
}
